import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import ddt from '@wix/da-ddt';
import { TabsManager } from './TabsManager';
import { FlagsObject } from '@wix/da-react-context/pkg/flags/types';

/**
 * RealtimeConnections is a singleton class that handles all realtime connections for the application.
 *
 * There are only ever 2 channels that are used:
 * - User channel
 * - Room channel
 *
 * The user channel is used for activity notifications
 * The room channel is used for message broadcasts, presence tracking, and typing indicators.
 *
 * Primary/Secondary Tab Management:
 * - Primary tab: Never disconnects on hidden, broadcasts stale to other tabs
 * - Secondary tabs: Disconnect after 1 minute when hidden, receive broadcasts from primary tab
 * - Secondary tabs marked as stale when primary receives activity, refresh when shown
 * - Tabs as grouped into pwa and non-pwa tabs, this allows pwa to maintain longer connection
 *
 * If this works well, we can extend this later to have the primary tab maintain a single RT
 * connection and then broadcast RT events to the secondary tabs. For now we keep it simple as
 * that will require more complex communication logic.
 *
 * Notes:
 * - We don't start the sub if the page is hidden (alt+click to open in a new tab)
 * - We schedule retry when channel is errored
 * - We reconnect when page is visible again and always attempt to fix lost connections
 * - We track failures with time windows to prevent excessive retries (10 retries in 30s)
 * - We track external failures (drawer & messages) that happen when page is hidden, then trigger a refresh on page show
 */
class RealtimeConnections {
  private client: SupabaseClient;
  private unsubscribeTimer: NodeJS.Timeout | null = null;
  private disconnectScheduled: boolean = false;

  private userChannel: RealtimeChannel | undefined;
  private userChannelEmitter: any;
  private userChannelTopic: string;
  private isUserChannelSubscribed: boolean = false;

  private roomChannel: RealtimeChannel | undefined;
  private roomChannelEmitter: any = null;
  private currentRoomId: string | undefined;
  private roomUserId: string;
  private isRoomChannelSubscribed: boolean = false;

  // Time-based failure tracking
  private userChannelFailures: Array<number> = []; // Timestamps of failures
  private roomChannelFailures: Array<number> = []; // Timestamps of failures
  private readonly MAX_RETRY_ATTEMPTS = 10;
  private readonly RETRY_TIME_WINDOW_MS = 30 * 1000; // 30 seconds

  // Track if room channel was disconnected due to timeout
  private wasRoomChannelDisconnected: boolean = false;

  // Callback to notify Redux when reconnecting after disconnect
  private onReconnectAfterDisconnect: (() => void) | null = null;

  // Tabs-broadcast state
  public tabsManager: TabsManager;
  private isStale: boolean = false;

  // Callback to notify Redux when session is lost
  private onSessionLost: ((reconnect: () => Promise<void>) => void) | null =
    null;
  private isSessionLost: boolean = false;

  // Track external failures, so we can refresh on page show
  private isConversationFetchFailed: boolean = false;
  private isMessageFetchFailed: boolean = false;

  // Feature flags
  private flags: FlagsObject;

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    this.setupTabsBroadcast();
    this.watchPageState();
  }

  public setFlags(flags: FlagsObject) {
    this.flags = flags;
  }

  public setConversationFetchFailed() {
    if (!document.hidden) {
      return;
    }
    this.isConversationFetchFailed = true;
  }

  public setMessageFetchFailed() {
    if (!document.hidden) {
      return;
    }
    this.isMessageFetchFailed = true;
  }

  /**
   * Setup tabs-broadcast event listeners
   */
  private setupTabsBroadcast() {
    const tabsManager = new TabsManager({
      channelName: 'damz-realtime-broadcast',
      onBecomePrimary: (detail: any) => {
        ddt.log('damz', 'Tab became primary', detail);
        this.isStale = false;

        // Cancel any pending disconnect timers
        if (this.unsubscribeTimer) {
          clearTimeout(this.unsubscribeTimer);
          this.unsubscribeTimer = null;
        }

        // Ensure channels are connected if page is visible and wasn't primary before
        this.handleReconnection();
      },
    });
    this.tabsManager = tabsManager;

    // Listen for stale notifications from primary tab
    tabsManager.on('stale-notification', (data: any) => {
      ddt.log('damz', 'Received stale notification from primary tab', data);
      if (!this.tabsManager.isPrimary) {
        this.isStale = true;
      }
    });
  }

  /**
   * Add a failure timestamp for user channel and check if we should stop retrying
   * @returns true if we should stop retrying (max failures in time window reached)
   */
  private addUserChannelFailure(): boolean {
    const now = Date.now();
    this.userChannelFailures.push(now);

    // Remove failures older than the time window
    this.userChannelFailures = this.userChannelFailures.filter(
      timestamp => now - timestamp <= this.RETRY_TIME_WINDOW_MS
    );

    // Check if we've exceeded the limit within the time window
    if (this.userChannelFailures.length > this.MAX_RETRY_ATTEMPTS) {
      ddt.alert(
        'damz',
        `User channel exceeded ${this.MAX_RETRY_ATTEMPTS} retries in ${this.RETRY_TIME_WINDOW_MS / 1000}s, stopping retry attempts`,
        ['damz', 'realtime'],
        {
          failureCount: this.userChannelFailures.length,
          timeWindow: this.RETRY_TIME_WINDOW_MS,
        }
      );
      return true; // Stop retrying
    }

    return false; // Continue retrying
  }

  /**
   * Add a failure timestamp for room channel and check if we should stop retrying
   * @returns true if we should stop retrying (max failures in time window reached)
   */
  private addRoomChannelFailure(): boolean {
    const now = Date.now();
    this.roomChannelFailures.push(now);

    // Remove failures older than the time window
    this.roomChannelFailures = this.roomChannelFailures.filter(
      timestamp => now - timestamp <= this.RETRY_TIME_WINDOW_MS
    );

    // Check if we've exceeded the limit within the time window
    if (this.roomChannelFailures.length > this.MAX_RETRY_ATTEMPTS) {
      ddt.alert(
        'damz',
        `Room channel exceeded ${this.MAX_RETRY_ATTEMPTS} retries in ${this.RETRY_TIME_WINDOW_MS / 1000}s, stopping retry attempts`,
        ['damz', 'realtime'],
        {
          failureCount: this.roomChannelFailures.length,
          timeWindow: this.RETRY_TIME_WINDOW_MS,
        }
      );
      return true; // Stop retrying
    }

    return false; // Continue retrying
  }

  /**
   * Reset user channel failure counter on successful connection
   */
  private resetUserChannelFailures(): void {
    this.userChannelFailures = [];
    ddt.log('damz', 'User channel failures reset due to successful connection');
  }

  /**
   * Reset room channel failure counter on successful connection
   */
  private resetRoomChannelFailures(): void {
    this.roomChannelFailures = [];
    ddt.log('damz', 'Room channel failures reset due to successful connection');
  }

  public setClient(client: SupabaseClient) {
    this.client = client;
    this.client.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // session ended — it might be due to refresh failure or manual sign-out
        // for manual sign-out the session fetch will fail so this is safe
        ddt.log('damz', 'SIGNED_OUT event detected', { session });
        this.isSessionLost = true;

        // Notify Redux saga to fetch new session and reconnect
        if (this.onSessionLost) {
          // Check if user is online before triggering session lost
          if (window.navigator.onLine) {
            this.onSessionLost(async () => {
              this.isSessionLost = false;
              this.handleReconnection();
            });
          } else {
            // User is offline, wait for them to come back online
            ddt.log(
              'damz',
              'User is offline, waiting for online before session recovery'
            );
            const onlineHandler = () => {
              window.removeEventListener('online', onlineHandler);
              if (this.onSessionLost) {
                ddt.log(
                  'damz',
                  'User back online, triggering session recovery'
                );
                this.onSessionLost(async () => {
                  this.isSessionLost = false;
                  this.handleReconnection();
                });
              }
            };
            window.addEventListener('online', onlineHandler);
          }
        }
      }
    });
  }

  public getClient() {
    return this.client;
  }

  public setReconnectCallback(callback: (() => void) | null) {
    this.onReconnectAfterDisconnect = callback;
  }

  public setSessionLostCallback(
    callback: ((reconnect: () => Promise<void>) => void) | null
  ) {
    this.onSessionLost = callback;
  }

  public startUserChannel(userChannel: string) {
    // If already subscribed or page is hidden, don't start again
    if (document.hidden || this.isUserChannelSubscribed) {
      ddt.log(
        'damz',
        'Realtime user channel not started, already subscribed or page is hidden'
      );
      return emitter => {
        return this.setUserChannelEmitter(emitter);
      };
    }

    this.userChannelTopic = userChannel;
    this.userChannel = this.client.realtime.channel(`user:${userChannel}`, {
      config: {
        broadcast: {
          ack: false,
          self: false,
        },
      },
    });
    this.userChannel.on('broadcast', { event: 'activity' }, data => {
      this.userChannelEmitter(data);
      this.broadcastActivityReceived();
    });
    this.userChannel.subscribe((status, err) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        // Only alert and schedule timeout if document is not hidden
        if (!document.hidden) {
          if (this.addUserChannelFailure()) {
            return; // Stop retrying - max failures in time window reached
          }
          ddt.log(
            'damz',
            'Channel error detected, triggering recovery',
            ['damz', 'realtime'],
            {
              status,
              err,
              tabState: document.hidden,
              recentFailures: this.userChannelFailures.length,
            }
          );
          this.userChannel?.rejoinTimer?.scheduleTimeout();
        }
      }

      if (status === 'SUBSCRIBED') {
        this.isUserChannelSubscribed = true;
        this.resetUserChannelFailures();
      }

      if (status === 'CLOSED') {
        this.isUserChannelSubscribed = false;
        this.resetUserChannelFailures();
      }

      if (err && err.message !== '') {
        if (Math.floor(Math.random() * 100) === 0) {
          ddt.alert(
            'damz',
            `Realtime subscription error for userChannel - ${status}`,
            ['damz', 'realtime'],
            {
              status,
              errMessage: err.message,
              err,
            }
          );
        }
      }
    });

    return emitter => {
      return this.setUserChannelEmitter(emitter);
    };
  }

  public startRoomChannel(roomId: string, userId: string) {
    // Validate roomId parameter
    if (!roomId) {
      ddt.log(
        'damz',
        'Cannot start room channel - roomId is undefined or empty'
      );
      return emitter => {
        return this.setRoomChannelEmitter(emitter);
      };
    }

    // If page is hidden, don't start room channels
    if (document.hidden || this.isRoomChannelSubscribed) {
      ddt.log(
        'damz',
        'Realtime room channel not started, page is hidden or already subscribed',
        roomId
      );
      return emitter => {
        return this.setRoomChannelEmitter(emitter);
      };
    }

    this.currentRoomId = roomId;
    this.roomUserId = userId;
    this.roomChannel = this.client.realtime.channel(roomId, {
      config: {
        private: true,
        broadcast: {
          ack: false, // don't wait for ack
          self: false, // don't send to self
        },
        presence: {
          key: roomId, // Track everything under single key
        },
      },
    });

    // Set up event handlers
    this.roomChannel
      .on('broadcast', { event: 'message' }, data => {
        if (this.roomChannelEmitter) {
          this.roomChannelEmitter({ type: 'message', data });
        }
      })
      .on('broadcast', { event: 'update' }, data => {
        if (this.roomChannelEmitter) {
          this.roomChannelEmitter({ type: 'update', data });
        }
      })
      .on('presence', { event: 'join' }, data => {
        if (this.roomChannelEmitter) {
          this.roomChannelEmitter({ type: 'presence_join', data });
        }
      })
      .on('presence', { event: 'leave' }, data => {
        if (this.roomChannelEmitter) {
          this.roomChannelEmitter({ type: 'presence_leave', data });
        }
      })
      .on('presence', { event: 'sync' }, () => {
        if (this.roomChannel) {
          const newState = this.roomChannel.presenceState();
          if (this.roomChannelEmitter) {
            this.roomChannelEmitter({ type: 'presence_sync', newState });
          }
        }
      })
      .subscribe(async (status, err) => {
        ddt.log(
          'damz',
          `Realtime subscription status changed to: ${status} for roomChannel`,
          {
            roomId,
            status,
            err,
          }
        );

        if (err) {
          ddt.error(
            'damz',
            `Realtime subscription error (status: ${status}) for roomChannel`,
            err
          );
        }

        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Only alert and schedule timeout if document is not hidden
          if (!document.hidden) {
            if (this.addRoomChannelFailure()) {
              return; // Stop retrying - max failures in time window reached
            }
            ddt.log(
              'damz',
              'Channel error detected, triggering recovery',
              ['damz', 'realtime'],
              {
                status,
                err,
                tabState: document.hidden,
                recentFailures: this.roomChannelFailures.length,
              }
            );
            this.roomChannel?.rejoinTimer?.scheduleTimeout();
          }
        }

        if (status === 'SUBSCRIBED') {
          this.isRoomChannelSubscribed = true;
          this.resetRoomChannelFailures();
          ddt.log(
            'damz',
            'Successfully SUBSCRIBED to room, starting presence tracking',
            roomId
          );

          // Track presence
          if (this.roomChannel && !this.flags?.['damz_ks_presence']) {
            await this.roomChannel.track({
              user_id: userId,
            });
          }
        }

        if (status === 'CLOSED') {
          this.resetRoomChannelFailures();
          this.isRoomChannelSubscribed = false;
        }

        if (err && err.message !== '') {
          if (Math.floor(Math.random() * 100) === 0) {
            ddt.alert(
              'damz',
              `Realtime subscription error for roomChannel - ${status}`,
              ['damz', 'realtime'],
              {
                status,
                errMessage: err.message,
                err,
              }
            );
          }
        }
      });

    return emitter => {
      return this.setRoomChannelEmitter(emitter);
    };
  }

  public async roomTrack(data) {
    if (this.flags?.['damz_ks_presence']) {
      return;
    }
    return this.roomChannel?.track(data);
  }

  public async roomUnTrack() {
    if (this.flags?.['damz_ks_presence']) {
      return;
    }
    return this.roomChannel?.untrack();
  }

  public async refreshSession() {
    const { data, error } = await this.client.auth.getSession();
    if (error || !data.session) {
      ddt.alert('damz', 'Failed to get session RC', ['damz', 'realtime'], {
        error: error ? error.message : 'unknown',
        errorCode: error ? error.code : 'unknown',
        errorStatus: error ? error.status : 'unknown',
        data,
      });
      return;
    }
    if (this.client.realtime.accessTokenValue !== data.session.access_token) {
      await this.client.realtime.setAuth();
      ddt.log('damz.auth', 'Refreshed session');
    }
  }

  public async reconnect() {
    await this.refreshSession();

    // Reset failure counters on manual reconnect
    this.resetUserChannelFailures();
    this.resetRoomChannelFailures();

    // Reconnect user channel
    this.userChannel?.unsubscribe();
    if (this.userChannel) {
      this.getClient().removeChannel(this.userChannel);
    }
    this.isUserChannelSubscribed = false;
    this.startUserChannel(this.userChannelTopic);

    // Reconnect room channel if one exists
    this.roomChannel?.unsubscribe();
    if (this.roomChannel) {
      this.getClient().removeChannel(this.roomChannel);
    }
    this.isRoomChannelSubscribed = false;

    // Only reconnect room channel if we have a valid roomId
    if (this.currentRoomId) {
      this.startRoomChannel(this.currentRoomId, this.roomUserId);
    } else {
      ddt.log('damz', 'Skipping room channel reconnect - no current roomId');
    }
  }

  public disconnectRoomChannel() {
    this.roomChannel?.untrack();
    this.roomChannel?.unsubscribe();
    if (this.roomChannel) {
      this.client.removeChannel(this.roomChannel);
    }
    this.isRoomChannelSubscribed = false;
    this.currentRoomId = undefined;
  }

  /**
   * Broadcast activity notification to non-primary tabs (only called by primary tab)
   */
  public broadcastActivityReceived() {
    // Only broadcast if this is the primary tab
    if (!this.tabsManager.isPrimary) {
      return;
    }

    this.tabsManager.emit('stale-notification', { timestamp: Date.now() });
    ddt.log('damz', 'Broadcasting stale notification to non-primary tabs');
  }

  private setUserChannelEmitter(emitter: any) {
    this.userChannelEmitter = emitter;

    return () => {
      this.userChannelEmitter = null;
      this.userChannel?.unsubscribe();
      if (this.userChannel) {
        this.getClient().removeChannel(this.userChannel);
      }
      this.isUserChannelSubscribed = false;
    };
  }

  private setRoomChannelEmitter(emitter: any) {
    this.roomChannelEmitter = emitter;

    return () => {
      this.roomChannelEmitter = null;
      this.roomChannel?.untrack();
      this.roomChannel?.unsubscribe();
      if (this.roomChannel) {
        this.client.removeChannel(this.roomChannel);
      }
      this.isRoomChannelSubscribed = false;
      this.currentRoomId = undefined;
    };
  }

  /**
   * Immediately disconnect channels (used for offline events)
   */
  private disconnectChannels() {
    if (this.isUserChannelSubscribed && this.userChannel) {
      this.userChannel.unsubscribe();
      this.getClient().removeChannel(this.userChannel);
      this.isUserChannelSubscribed = false;
      ddt.log('damz', 'User channel disconnected');
    }

    if (this.isRoomChannelSubscribed && this.roomChannel) {
      this.roomChannel.untrack();
      this.roomChannel.unsubscribe();
      this.getClient().removeChannel(this.roomChannel);
      this.isRoomChannelSubscribed = false;
      this.wasRoomChannelDisconnected = true;
      ddt.log('damz', 'Room channel disconnected');
    }
  }

  /**
   * Schedule delayed disconnection (used for visibility change and pagehide)
   */
  private scheduleDisconnect() {
    // Primary tabs never disconnect on hidden
    if (this.tabsManager.isPrimary) {
      ddt.log('damz', 'Primary tab - skipping disconnect schedule');
      return;
    }

    // Clear any existing timer
    if (this.unsubscribeTimer) {
      clearTimeout(this.unsubscribeTimer);
      this.unsubscribeTimer = null;
    }

    // Non-primary tabs: Wait 1 minute before unsubscribing
    const NON_PRIMARY_DELAY_MS = 60 * 1000; // 1 minute
    this.unsubscribeTimer = setTimeout(() => {
      if (document.hidden && this.isUserChannelSubscribed && this.userChannel) {
        this.userChannel.unsubscribe();
        this.getClient().removeChannel(this.userChannel);
        this.isUserChannelSubscribed = false;
        this.unsubscribeTimer = null;
        ddt.log(
          'damz',
          'Non-primary tab disconnected user channel after 1 minute'
        );
      }

      if (document.hidden && this.isRoomChannelSubscribed && this.roomChannel) {
        this.roomChannel.untrack();
        this.roomChannel.unsubscribe();
        this.getClient().removeChannel(this.roomChannel);
        this.isRoomChannelSubscribed = false;
        this.wasRoomChannelDisconnected = true;
        this.unsubscribeTimer = null;
        ddt.log(
          'damz',
          'Non-primary tab disconnected room channel after 1 minute'
        );
      }
    }, NON_PRIMARY_DELAY_MS);
  }

  /**
   * Handle reconnection logic (used for online, pageshow, and visibility change)
   */
  private handleReconnection() {
    if (this.isSessionLost) {
      ddt.log('damz', 'Session lost, skipping reconnection');
      return;
    }

    // Clear the unsubscribe timer since page is active again
    if (this.unsubscribeTimer) {
      clearTimeout(this.unsubscribeTimer);
      this.unsubscribeTimer = null;
    }

    if (!this.client) {
      ddt.log('damz', 'No client, skipping reconnection');
      return;
    }

    // Always attempt to fix lost connections
    if (this.isUserChannelSubscribed || this.isRoomChannelSubscribed) {
      this.refreshSession().catch(error => {
        ddt.error('damz', 'Failed to refresh session on reconnection', error);
      });
      const channels = this.client.getChannels();
      for (const ch of channels) {
        if (ch.state === 'errored') {
          ddt.log('damz.auth', 'Channel errored, scheduling rejoin', ch);
          ch.rejoinTimer.scheduleTimeout();
        }
      }
    }

    // If we unsubbed - resub
    if (!this.isUserChannelSubscribed || !this.isRoomChannelSubscribed) {
      this.reconnect().catch(error => {
        ddt.error('damz', 'Failed to reconnect on page active', error);
      });
    }

    // If room channel was disconnected due to timeout, notify Redux to reload active room
    // or we have failed fetches
    const shouldReconnect =
      this.wasRoomChannelDisconnected ||
      this.isStale ||
      this.isConversationFetchFailed ||
      this.isMessageFetchFailed;

    if (shouldReconnect && this.onReconnectAfterDisconnect) {
      this.onReconnectAfterDisconnect();
      this.wasRoomChannelDisconnected = false;
      this.isConversationFetchFailed = false;
      this.isMessageFetchFailed = false;
      this.isStale = false;
    }

    // Reset stale flag after handling reconnection
    if (this.isStale) {
      this.isStale = false;
    }
  }

  private scheduleOnce(reason: string) {
    if (this.disconnectScheduled) {
      return;
    }
    this.disconnectScheduled = true;
    queueMicrotask(() => {
      this.scheduleDisconnect();
      this.disconnectScheduled = false;
    });
    ddt.log('damz', `Scheduling disconnect due to ${reason}`);
  }

  private watchPageState() {
    // Offline: disconnect immediately
    window.addEventListener('offline', () => {
      ddt.log(
        'damz',
        'Browser went offline, disconnecting channels immediately'
      );
      this.disconnectChannels();
    });

    // Online: reconnect
    window.addEventListener('online', () => {
      ddt.log('damz', 'Browser came online, attempting to reconnect');
      this.handleReconnection();
    });

    // Page hide: schedule disconnect with timer
    window.addEventListener('pagehide', () => {
      ddt.log('damz', 'Page hidden, scheduling disconnect');
      this.scheduleOnce('pagehide');
    });

    // Page show: reconnect if needed
    window.addEventListener('pageshow', () => {
      ddt.log('damz', 'Page shown, checking reconnection');
      this.handleReconnection();
    });

    // Visibility change: use timer for disconnect, immediate for reconnect
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        ddt.log('damz', 'Document hidden, scheduling disconnect');
        this.scheduleOnce('visibilitychange');
      } else {
        ddt.log('damz', 'Document visible, checking reconnection');
        this.handleReconnection();
      }
    });
  }
}

export const realtimeConnections = new RealtimeConnections();
